import { renderImageObject, syncImageObjectOnGroup } from "./renderers/image";
import { renderAudioObject, syncAudioObjectOnGroup, type AudioAction } from "./renderers/audio";
import {
  renderPdfDocumentObject,
  syncPdfDocumentOnGroup,
  pdfDomMatchesObject,
  pdfSurfaceRight,
  rememberPdfThumbsScroll,
  restorePdfThumbsScroll,
} from "./renderers/pdfDocument";
import { renderTimerObject, type TimerAction } from "./renderers/timer";
import { renderCalculatorObject } from "./renderers/calculator";
import {
  renderCodeObject,
  syncCodeObjectOnGroup,
} from "./renderers/code";
import {
  renderSqlObject,
  syncSqlObjectOnGroup,
} from "./renderers/sql";
import type { CalculatorAction } from "../../lib/board/calculatorLogic";
import type { CodeAction } from "../../lib/board/codeLogic";
import type { SqlAction } from "../../lib/board/sqlLogic";
import { renderFrameObject } from "./renderers/frame";
import { shouldRenderSceneObject, buildSceneObjectMap } from "../document/documentContext";
import { renderPathObject, syncPathObjectOnGroup } from "./renderers/path";
import { renderShapeObject, isShapeObject, syncShapeOnGroup } from "./shapes/syncShape";
import { renderSegmentObject } from "./renderers/segment";
import {
  renderStickyObject,
  renderTextObject,
  syncTextLikeOnGroup,
} from "./renderers/textObject";
import type { SceneObject } from "../SceneHost";
import { sortSceneObjects } from "./sceneSort";
import { unionBounds } from "../selectionBounds";

export type SvgRenderContext = {
  assetContentUrl: (assetId: string) => string;
  pagePreviewUrl?: (objectId: string, pageIndex: number) => string | null;
  useCredentials?: boolean;
  canEdit?: boolean;
  textEditingId?: string | null;
  canPdfPageChange?: (objectId: string) => boolean;
  onPdfPageChange?: (objectId: string, page: number) => void;
  onTimerAction?: (objectId: string, action: TimerAction, payload?: { durationMs?: number }) => void;
  onTimerSelect?: (objectId: string) => void;
  onCalculatorAction?: (objectId: string, action: CalculatorAction) => void;
  onCalculatorSelect?: (objectId: string) => void;
  onCodeAction?: (objectId: string, action: CodeAction) => void;
  onCodeSelect?: (objectId: string) => void;
  onSqlAction?: (objectId: string, action: SqlAction) => void;
  onSqlSelect?: (objectId: string) => void;
  onAudioAction?: (objectId: string, action: AudioAction) => void;
  /** Полный список объектов сцены: размер слоя не должен зависеть от отсечения. */
  surfaceObjects?: SceneObject[];
};

/** Типы, где переиспользование DOM заметно ускоряет сцену (нет пересоздания узлов). */
const PRESERVE_DOM_TYPES = new Set([
  "text",
  "sticky",
  "image",
  "code",
  "sql",
  "audio",
  "pdf_document",
  "path",
  "rect",
  "ellipse",
  "triangle",
  "diamond",
  "star",
  "geom",
]);

function sceneObjTypeClass(type: string): string {
  if (type === "pdf_document") return "wb-scene-obj--pdf";
  return `wb-scene-obj--${type}`;
}

function existingMatchesType(el: SVGGElement, type: string): boolean {
  return el.classList.contains(sceneObjTypeClass(type));
}

export function renderSceneObjectSvg(
  obj: SceneObject,
  _ctx: SvgRenderContext,
  defs?: SVGDefsElement
): SVGGElement | null {
  if (obj.type === "frame") return renderFrameObject(obj);
  if (obj.type === "image") return renderImageObject(obj, _ctx.assetContentUrl);
  if (obj.type === "path") return renderPathObject(obj);
  if (obj.type === "line") return renderSegmentObject(obj, { type: "line", defs });
  if (obj.type === "arrow") return renderSegmentObject(obj, { type: "arrow", defs });
  if (isShapeObject(obj)) return renderShapeObject(obj);
  return null;
}

function trySyncPreservedGroup(
  g: SVGGElement,
  obj: SceneObject,
  _ctx: SvgRenderContext
): boolean {
  if (obj.type === "image") {
    return syncImageObjectOnGroup(g, obj, _ctx.assetContentUrl);
  }
  if (obj.type === "path") {
    return syncPathObjectOnGroup(g, obj);
  }
  if (isShapeObject(obj)) {
    syncShapeOnGroup(g, obj);
    return true;
  }
  return false;
}

export function renderSvgSceneLayer(
  svg: SVGSVGElement,
  objects: SceneObject[],
  ctx: SvgRenderContext
): void {
  // Сюда попадают и группы SVG, и окна объектов с вёрсткой: без окон карта
  // для текста, стикеров, картинок и презентаций оставалась пустой, и каждый
  // пересчёт сцены пересоздавал их целиком — отсюда рывки при перетаскивании.
  const existing = new Map<string, Element>();
  for (const child of Array.from(svg.children)) {
    const isItem = child instanceof SVGGElement || child.classList.contains(HTML_ITEM_CLASS);
    if (!isItem) continue;
    const id = child.getAttribute("data-object-id");
    if (id) existing.set(id, child);
  }

  const nextChildren: ChildNode[] = [];
  const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");

  const sorted = sortSceneObjects(objects as Record<string, unknown>[]);
  const objectMap = buildSceneObjectMap(objects);

  for (const raw of sorted) {
    const obj = raw as SceneObject;
    if (!shouldRenderSceneObject(obj, objectMap)) continue;
    // HTML-объект: своё окно в общем порядке слоёв.
    if (HTML_OBJECT_TYPES.has(obj.type)) {
      try {
        const prev = existing.get(obj.id);
        const prevNode = prev ? htmlItemNode(prev) : null;
        if (prev && prevNode && syncHtmlObject(prevNode, obj, ctx)) {
          nextChildren.push(prev);
          continue;
        }
        const node = renderHtmlObject(obj, ctx);
        if (!node) {
          if (prev) nextChildren.push(prev);
          continue;
        }
        const fo = createHtmlItem(obj.id);
        htmlItemInner(fo)?.appendChild(node);
        placeNewHtmlItem(svg, fo);
        nextChildren.push(fo);
      } catch (err) {
        if (typeof console !== "undefined" && console.error) {
          console.error("[bezpart-board] не удалось отрисовать объект", obj?.id, obj?.type, err);
        }
        const prev = existing.get(obj.id);
        if (prev) nextChildren.push(prev);
      }
      continue;
    }

    // Один повреждённый объект не должен ронять отрисовку всей доски.
    // Исключение отсюда раньше прерывало синхронизацию состояния целиком:
    // выделение не доходило до интерфейса, панель над объектами залипала,
    // а сцена переставала обновляться до перезагрузки страницы.
    try {
      const prevAny = existing.get(obj.id);
      const prev = prevAny instanceof SVGGElement ? prevAny : null;
      let reused = false;
      if (
        prev &&
        PRESERVE_DOM_TYPES.has(obj.type) &&
        existingMatchesType(prev, obj.type)
      ) {
        reused = trySyncPreservedGroup(prev, obj, ctx);
      }

      if (reused && prev) {
        nextChildren.push(prev);
        continue;
      }

      const node = renderSceneObjectSvg(obj, ctx, defs);
      if (node) nextChildren.push(node);
    } catch (err) {
      if (typeof console !== "undefined" && console.error) {
        console.error("[bezpart-board] не удалось отрисовать объект", obj?.id, obj?.type, err);
      }
      // Оставляем прошлый узел, если он был — объект не мигнёт и не исчезнет.
      const prev = existing.get(obj.id);
      if (prev) nextChildren.push(prev);
    }
  }

  // Раньше слой пересобирался целиком: снять всех детей и вставить заново.
  // Это N удалений + N вставок на КАЖДУЮ правку сцены, даже когда сдвинулся
  // один стикер — отсюда просадка, растущая с числом объектов на доске.
  // Теперь трогаем только те позиции, где порядок реально разошёлся:
  // при неизменном порядке DOM не меняется вообще.
  rememberPdfThumbsScroll(svg);

  // defs пересоздаётся каждый кадр (маркеры стрелок) — меняем на месте,
  // иначе несовпадение первого узла сдвинуло бы весь список.
  const oldDefs = svg.firstChild;
  if (oldDefs && oldDefs.nodeName === "defs") {
    svg.replaceChild(defs, oldDefs);
  } else {
    svg.insertBefore(defs, oldDefs);
  }

  let cursor: ChildNode | null = defs.nextSibling;
  for (const node of nextChildren) {
    if (cursor === node) {
      cursor = cursor.nextSibling;
      continue;
    }
    // insertBefore переносит уже существующий узел — foreignObject/img живут дальше.
    svg.insertBefore(node, cursor);
  }
  while (cursor) {
    const next: ChildNode | null = cursor.nextSibling;
    svg.removeChild(cursor);
    cursor = next;
  }

  restorePdfThumbsScroll(svg);

  syncSceneLayerSurface(svg, ctx.surfaceObjects ?? objects);
}

/**
 * Типы объектов, которые рисуются обычным HTML.
 *
 * Такой объект занимает в сцене своё окно `foreignObject`, и это окно
 * стоит РОВНО В НАЧАЛЕ слоя, а место объекту внутри задаётся обычным CSS.
 *
 * Зачем так: Safari (а на iPhone и iPad движок один у всех браузеров)
 * рисует содержимое `foreignObject` от левого верхнего угла слоя, не
 * применяя ни систему координат сцены, ни сдвиг объекта. Если окно и так
 * стоит в начале слоя, оба браузера кладут содержимое в одно и то же место.
 * Порядок слоёв при этом обычный: окно — такой же ребёнок сцены, как и
 * вектор, поэтому надпись поверх рамки остаётся поверх рамки.
 */
const HTML_OBJECT_TYPES = new Set([
  "pdf_document",
  "audio",
  "text",
  "sticky",
  // Картинки здесь нет нарочно: ей вёрстка не нужна, а в своём окне она на
  // iOS рисовалась поверх всего слоя — штрих поверх картинки уходил под неё.
  "timer",
  "calculator",
  "code",
  "sql",
]);

const HTML_ITEM_CLASS = "wb-scene-html-item";

/** Окно для одного HTML-объекта: само окно — во весь слой, объект — внутри. */
function createHtmlItem(objectId: string): SVGForeignObjectElement {
  const fo = document.createElementNS(
    "http://www.w3.org/2000/svg",
    "foreignObject"
  ) as SVGForeignObjectElement;
  fo.setAttribute("class", HTML_ITEM_CLASS);
  fo.setAttribute("data-object-id", objectId);
  const inner = document.createElement("div");
  inner.className = "wb-scene-html-item__inner";
  // Сквозная для указателя подложка: клики достаются объектам под ней.
  inner.style.cssText =
    "position:absolute;left:0;top:0;width:0;height:0;pointer-events:none";
  fo.appendChild(inner);
  return fo;
}

function htmlItemInner(fo: Element): HTMLElement | null {
  const inner = fo.firstElementChild;
  return inner instanceof HTMLElement ? inner : null;
}

function htmlItemNode(fo: Element): HTMLElement | null {
  const inner = htmlItemInner(fo);
  const node = inner?.firstElementChild;
  return node instanceof HTMLElement ? node : null;
}

/**
 * Ставит окна HTML-объектов в начало слоя и сдвигает их подложку так,
 * чтобы объект внутри задавался обычными координатами сцены.
 */
function layoutHtmlItems(svg: SVGSVGElement, x: number, y: number, w: number, h: number): void {
  const items = svg.querySelectorAll("." + HTML_ITEM_CLASS);
  for (const fo of Array.from(items)) placeHtmlItem(fo, x, y, w, h);
}

function placeHtmlItem(fo: Element, x: number, y: number, w: number, h: number): void {
  fo.setAttribute("x", String(x));
  fo.setAttribute("y", String(y));
  fo.setAttribute("width", String(w));
  fo.setAttribute("height", String(h));
  const inner = htmlItemInner(fo);
  if (inner) {
    inner.style.left = `${-x}px`;
    inner.style.top = `${-y}px`;
  }
}

/** Последняя посчитанная поверхность слоя — чтобы не размечать её заново впустую. */
type Surface = { x: number; y: number; w: number; h: number };
const surfaces = new WeakMap<SVGSVGElement, Surface>();

function sameSurface(svg: SVGSVGElement, x: number, y: number, w: number, h: number): boolean {
  const prev = surfaces.get(svg);
  return Boolean(prev && prev.x === x && prev.y === y && prev.w === w && prev.h === h);
}

function rememberSurface(svg: SVGSVGElement, x: number, y: number, w: number, h: number): void {
  surfaces.set(svg, { x, y, w, h });
}

/** Новое окно объекта ставим сразу: общей разметки может и не случиться. */
function placeNewHtmlItem(svg: SVGSVGElement | null, fo: Element): void {
  const surface = svg ? surfaces.get(svg) : null;
  if (!surface) return;
  placeHtmlItem(fo, surface.x, surface.y, surface.w, surface.h);
}

function audioOptionsFor(ctx: SvgRenderContext) {
  return {
    assetContentUrl: ctx.assetContentUrl,
    useCredentials: ctx.useCredentials,
    canEdit: ctx.canEdit,
    onAudioAction: ctx.onAudioAction,
  };
}

/** Новый узел объекта для слоя HTML. */
function renderHtmlObject(obj: SceneObject, ctx: SvgRenderContext): HTMLElement | null {
  if (obj.type === "pdf_document") {
    return renderPdfDocumentObject(obj, pdfOptionsFor(obj, ctx));
  }
  if (obj.type === "audio") return renderAudioObject(obj, audioOptionsFor(ctx));
  if (obj.type === "text") return renderTextObject(obj);
  if (obj.type === "sticky") return renderStickyObject(obj);
  if (obj.type === "timer") {
    return renderTimerObject(obj, {
      canEdit: Boolean(ctx.canEdit),
      onTimerAction: ctx.onTimerAction,
      onRequestSelect: ctx.onTimerSelect,
    });
  }
  if (obj.type === "calculator") {
    return renderCalculatorObject(obj, {
      canEdit: Boolean(ctx.canEdit),
      onCalculatorAction: ctx.onCalculatorAction,
      onRequestSelect: ctx.onCalculatorSelect,
    });
  }
  if (obj.type === "code") {
    return renderCodeObject(obj, {
      canEdit: Boolean(ctx.canEdit),
      onCodeAction: ctx.onCodeAction,
      onRequestSelect: ctx.onCodeSelect,
    });
  }
  if (obj.type === "sql") {
    return renderSqlObject(obj, {
      canEdit: Boolean(ctx.canEdit),
      onSqlAction: ctx.onSqlAction,
      onRequestSelect: ctx.onSqlSelect,
    });
  }
  return null;
}

/** Обновление уже существующего узла; false — если проще пересобрать. */
function syncHtmlObject(
  el: HTMLElement,
  obj: SceneObject,
  ctx: SvgRenderContext
): boolean {
  if (obj.type === "text" || obj.type === "sticky") {
    syncTextLikeOnGroup(el, obj, ctx.textEditingId === obj.id);
    return true;
  }
  // Виджеты с редакторами обновляются на месте, в том числе у того, кто сейчас
  // в них печатает: чужой текст встаёт без потери курсора (liveTextSync).
  if (obj.type === "code") return syncCodeObjectOnGroup(el, obj);
  if (obj.type === "sql") return syncSqlObjectOnGroup(el, obj);
  if (obj.type === "pdf_document") {
    if (!pdfDomMatchesObject(el, obj)) return false;
    return syncPdfDocumentOnGroup(el, obj, pdfOptionsFor(obj, ctx));
  }
  if (obj.type === "audio") return syncAudioObjectOnGroup(el, obj, audioOptionsFor(ctx));
  return false;
}

function pdfOptionsFor(obj: SceneObject, ctx: SvgRenderContext) {
  return {
    assetContentUrl: ctx.assetContentUrl,
    pagePreviewUrl: ctx.pagePreviewUrl,
    canChangePage: ctx.canPdfPageChange
      ? ctx.canPdfPageChange(obj.id)
      : Boolean(ctx.canEdit),
    onPageChange: ctx.onPdfPageChange,
  };
}


/** Отступ вокруг объектов, чтобы штрих/тени не обрезались. */
const SURFACE_PAD = 64;

/**
 * Поверхность округляется до этой сетки.
 *
 * Пересчёт идёт на каждом движении мыши при перетаскивании, а переразметка
 * поверхности трогает окна всех объектов с вёрсткой — на каждом кадре это
 * дорого (в WebKit кадр вырастал с 15 до 92 мс). С округлением размеры
 * меняются раз в несколько сотен пикселей пути, а не на каждом движении.
 */
const SURFACE_GRID = 512;

function floorTo(v: number, step: number): number {
  return Math.floor(v / step) * step;
}

function ceilTo(v: number, step: number): number {
  return Math.ceil(v / step) * step;
}

/**
 * Даёт SVG реальный CSS-размер по bbox объектов.
 * Иначе слой 0×0 с overflow + CSS scale даёт мыльный первый кадр в Chromium.
 */
/**
 * Слой сцены по узлу объекта. У объектов с вёрсткой узел — обычный HTML
 * внутри окна слоя, и `ownerSVGElement` у него нет; поиск по предкам находит
 * слой в обоих случаях.
 */
export function sceneLayerSvgFor(el: Element | null | undefined): SVGSVGElement | null {
  if (!el) return null;
  if (el instanceof SVGElement && el.ownerSVGElement) return el.ownerSVGElement;
  const svg = el.closest("svg");
  return svg instanceof SVGSVGElement ? svg : null;
}

export function syncSceneLayerSurface(svg: SVGSVGElement, objects: SceneObject[]): void {
  const union = unionBounds(objects);
  if (!union || union.w < 1 || union.h < 1) {
    svg.style.left = "0px";
    svg.style.top = "0px";
    svg.style.width = "1px";
    svg.style.height = "1px";
    svg.setAttribute("width", "1");
    svg.setAttribute("height", "1");
    svg.setAttribute("viewBox", "0 0 1 1");
    rememberSurface(svg, 0, 0, 1, 1);
    layoutHtmlItems(svg, 0, 0, 1, 1);
    return;
  }

  // Презентация шире своего bbox: справа у неё полоска слайдов.
  let right = union.x + union.w;
  for (const obj of objects) {
    if (obj.type === "pdf_document") right = Math.max(right, pdfSurfaceRight(obj));
  }

  const x = floorTo(union.x - SURFACE_PAD, SURFACE_GRID);
  const y = floorTo(union.y - SURFACE_PAD, SURFACE_GRID);
  const w = Math.max(1, ceilTo(right + SURFACE_PAD, SURFACE_GRID) - x);
  const h = Math.max(1, ceilTo(union.y + union.h + SURFACE_PAD, SURFACE_GRID) - y);

  // Поверхность пересчитывается на каждом движении мыши при перетаскивании, а
  // размеры её меняются редко. Без этой проверки каждое движение переразмечало
  // все объекты с вёрсткой — на доске с десятками объектов перетаскивание
  // начинало дёргаться.
  if (sameSurface(svg, x, y, w, h)) return;
  rememberSurface(svg, x, y, w, h);

  svg.style.left = `${x}px`;
  svg.style.top = `${y}px`;
  svg.style.width = `${w}px`;
  svg.style.height = `${h}px`;
  svg.setAttribute("width", String(w));
  svg.setAttribute("height", String(h));
  svg.setAttribute("viewBox", `${x} ${y} ${w} ${h}`);
  layoutHtmlItems(svg, x, y, w, h);
}

export function createSceneSvgLayer(): SVGSVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.classList.add("wb-board-scene-layer");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("overflow", "visible");
  svg.setAttribute("width", "1");
  svg.setAttribute("height", "1");
  svg.setAttribute("viewBox", "0 0 1 1");
  svg.style.left = "0px";
  svg.style.top = "0px";
  svg.style.width = "1px";
  svg.style.height = "1px";
  return svg;
}

export function sceneSvgDefs(layer: HTMLElement): SVGDefsElement | undefined {
  if (!(layer instanceof SVGSVGElement)) return undefined;
  return layer.querySelector("defs") ?? undefined;
}
